import React, { useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./Admin.css";

/* ================== CONSTANTS ================== */
const GITHUB_USERNAME = "parulshingade39-cmd";
const REPO_NAME = "data";
const BRANCH = "main";
const ACCESS_TOKEN =
  "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"; // move to .env later

const ADMIN_PASSWORD = "weddmac@123";

/* ================== HELPERS ================== */

// Read file as base64
const readBase64 = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result.split(",")[1]);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

// Upload file to GitHub
const uploadFile = async (path, base64, message, sha = null) => {
  const res = await axios.put(
    `https://api.github.com/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/${path}`,
    {
      message,
      content: base64,
      branch: BRANCH,
      ...(sha && { sha }),
    },
    {
      headers: {
        Authorization: `Bearer ${ACCESS_TOKEN}`,
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

// Upload in batches
const uploadInBatches = async (tasks, batchSize = 4) => {
  const results = [];
  for (let i = 0; i < tasks.length; i += batchSize) {
    const batch = tasks.slice(i, i + batchSize);
    const res = await Promise.all(batch.map((fn) => fn()));
    results.push(...res);
  }
  return results;
};

/* ================== COMPONENT ================== */
export default function Admin() {
  const navigate = useNavigate();

  /* 🔐 AUTH STATES */
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");

  /* 📦 FORM STATES */
  const [clientName, setClientName] = useState("");
  const [eventName, setEventName] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  /* 🔐 PASSWORD CHECK */
  const handlePasswordSubmit = () => {
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError("");
    } else {
      setPasswordError("Incorrect admin password");
    }
  };

  /* ================== SUBMIT ================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientName || !coverFile) {
      alert("Client name & cover image required");
      return;
    }

    setUploading(true);

    try {
      const clientId = clientName.toLowerCase().replace(/\s+/g, "_");
      const eventId = eventName?.toLowerCase().replace(/\s+/g, "_");
      const clientJsonPath = `clients/${clientId}/${clientId}.json`;

      let clientJson;
      let clientSha = null;

      try {
        const res = await axios.get(
          `https://api.github.com/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/${clientJsonPath}`,
          { headers: { Authorization: `Bearer ${ACCESS_TOKEN}` } }
        );

        clientJson = JSON.parse(atob(res.data.content));
        clientSha = res.data.sha;
      } catch {
        clientJson = {
          client: clientName,
          clientId,
          coverImage: "",
          events: [],
        };
      }

      /* COVER IMAGE */
      if (!clientJson.coverImage && coverFile) {
        const compressedCover = await imageCompression(coverFile, {
          maxSizeMB: 1.2,
          maxWidthOrHeight: 2400,
          fileType: "image/jpeg",
          initialQuality: 0.8,
          useWebWorker: true,
        });

        const coverBase64 = await readBase64(compressedCover);
        const coverPath = `clients/${clientId}/cover/cover.jpg`;

        await uploadFile(coverPath, coverBase64, "Upload cover image");
        clientJson.coverImage = coverPath;
      }

      /* EVENT IMAGES */
      if (files.length && eventId) {
        let event = clientJson.events.find(
          (e) => e.eventName === eventId
        );

        if (!event) {
          event = { eventName: eventId, images: [] };
          clientJson.events.push(event);
        }

        const uploadTasks = files.map((file) => async () => {
          const compressed = await imageCompression(file, {
            maxSizeMB: 0.9,
            maxWidthOrHeight: 1920,
            fileType: "image/jpeg",
            initialQuality: 0.75,
            useWebWorker: true,
          });

          const base64 = await readBase64(compressed);
          const unique = crypto.randomUUID();
          const path = `clients/${clientId}/${eventId}/${unique}.jpg`;

          await uploadFile(path, base64, "Upload event image");
          return path;
        });

        const uploadedPaths = await uploadInBatches(uploadTasks, 4);
        event.images.push(...uploadedPaths);
      }

      await uploadFile(
        clientJsonPath,
        btoa(JSON.stringify(clientJson, null, 2)),
        "Update client json",
        clientSha
      );

      alert("Client saved successfully!");

      setClientName("");
      setEventName("");
      setCoverFile(null);
      setFiles([]);
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }

    setUploading(false);
  };

  return (
    <div className="admin-container">
      {!isAuthenticated && (
        <div className="password-overlay">
          <div className="password-modal">
            <h3>Admin Access</h3>
            

            <input
              type="password"
              placeholder="Enter admin password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
            />

            {passwordError && (
              <p className="error-text">{passwordError}</p>
            )}

            <button className="main-btn" onClick={handlePasswordSubmit}>
              Verify
            </button>
            <button className="main-btn" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>
      )}

      {isAuthenticated && (
        <>
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>

          <h2>Admin – Client Upload</h2>

          <form className="admin-form" onSubmit={handleSubmit}>
            <input
              placeholder="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              required
            />

            <label>Cover Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCoverFile(e.target.files[0])}
            />

            <input
              placeholder="Event Name (optional)"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />

            <label>Event Images</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles([...e.target.files])}
            />

            <button className="main-btn" disabled={uploading}>
              {uploading ? "Uploading..." : "Save Client"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
